"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { getCydecoreScopeStyle } from "@/lib/cydecore-theme-tokens"
import { getThemeDefinition } from "@/lib/theme-config"
import { useTheme } from "@/lib/theme-context"
import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      /* Above Command Center CYDEcore sticky header (z-[100]) and dashboard chrome */
      "fixed inset-0 z-[110] bg-[rgba(0,0,0,0.8)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    aria-hidden={false}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  overlayClassName?: string
  /** CYDEcore preset from program-management mocks: scoped tokens, bg2 shell, modal overlay blur. */
  variant?: "default" | "cydecore"
  /** `wide` drops the responsive max-width ladder (use for full-width modals like case cover-flow). */
  size?: "default" | "wide"
}

const dialogContentMaxWidthClasses = {
  default: "max-w-[95vw] sm:max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-5xl",
  wide: "max-w-[min(99vw,112rem)]",
} as const

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, style, overlayClassName, variant = "default", size = "default", ...props }, ref) => {
  const { theme } = useTheme()
  const category = getThemeDefinition(theme)?.category ?? "dark"
  const cydecoreScope = variant === "cydecore" ? getCydecoreScopeStyle(category) : null

  return (
  <DialogPortal>
    <DialogOverlay
      className={cn(variant === "cydecore" && "cydecore-dialog-overlay", overlayClassName)}
    />
    <DialogPrimitive.Content
      ref={ref}
      data-cydecore-dialog={variant === "cydecore" ? "" : undefined}
      data-theme-category={variant === "cydecore" ? category : undefined}
      className={cn(
        "fixed left-[50%] top-4 z-[120] grid w-full max-w-[95vw] translate-x-[-50%] gap-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain sm:top-[7vh]",
        variant === "default" &&
          cn(
            "border theme-border bg-background p-4 sm:p-6 sm:rounded-lg",
            dialogContentMaxWidthClasses[size],
          ),
        variant === "cydecore" &&
          "cydecore-dialog-content sm:max-w-[min(100vw-2rem,780px)] gap-0 border border-[color:var(--cydecore-border2)] bg-[color:var(--cydecore-bg2)] p-6 font-[family-name:var(--font-cc-sans),system-ui,sans-serif] text-[color:var(--cydecore-text)] sm:rounded-2xl",
        className
      )}
      style={{
        ...(variant === "default"
          ? { borderColor: "var(--theme-border)" }
          : { ...(cydecoreScope ?? {}) }),
        ...(style as React.CSSProperties)
      }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        className={cn(
          "absolute right-2 top-2 z-10 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none sm:right-4 sm:top-4",
          variant === "cydecore"
            ? "text-[color:var(--cydecore-text3)] ring-offset-[color:var(--cydecore-bg2)] hover:bg-[color:var(--cydecore-surface2)] hover:text-[color:var(--cydecore-text)] data-[state=open]:bg-transparent data-[state=open]:text-[color:var(--cydecore-text3)]"
            : "ring-offset-background data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
        )}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
